import { AlertTriangle, Bell } from 'lucide-react';
import React from 'react';
import { BRAND_LOGO_SQUARE } from '../brandAssets';
import { User } from '../types';
import { ProfileButton } from './ProfileButton';
import { Toast, ToastType } from './Toast';

interface LayoutProps {
    children: React.ReactNode;
    currentUser: User | null;
    viewMode: 'map' | 'list';
    unreadNotificationCount: number;
    onProfileClick: () => void;
    onNotificationsClick: () => void;
    toastProps: {
        message: string;
        type: ToastType;
        isVisible: boolean;
        onClose: () => void;
        action?: { label: string; onClick: () => void };
        duration?: number;
    };
    isMapError: boolean;
}

export const Layout: React.FC<LayoutProps> = ({
    children,
    currentUser,
    viewMode,
    unreadNotificationCount,
    onProfileClick,
    onNotificationsClick,
    toastProps,
    isMapError
}) => {
    return (
        <div className="w-full h-screen bg-brand-cream overflow-hidden relative flex flex-col">
            {/* TOP BAR */}
            <div className="absolute top-0 left-0 right-0 z-30 p-4 flex justify-between items-start pointer-events-none">
                {/* Left: Round logo badge — compact so it doesn't crowd the map */}
                <img
                    src={BRAND_LOGO_SQUARE}
                    alt="Good Shout logo"
                    className="w-12 h-12 rounded-full object-cover shadow-[0_4px_12px_rgba(55,65,81,0.18)]"
                />

                {/* Right: Notifications + Avatar */}
                {currentUser && (
                    <div className="flex items-center gap-2 pointer-events-auto">
                        {/* Notifications Bell */}
                        <button
                            className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95 relative border-2 border-white"
                            onClick={onNotificationsClick}
                            aria-label="Open Notifications"
                        >
                            <Bell size={20} className="text-brand-dark" />
                            {unreadNotificationCount > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-5 h-5 bg-brand-red text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-brand-cream px-1">
                                    {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                                </span>
                            )}
                        </button>

                        {/* Profile Button */}
                        <ProfileButton
                            onClick={onProfileClick}
                            photoURL={currentUser.photoURL}
                        />
                    </div>
                )}
            </div>

            {/* TOAST */}
            <Toast
                message={toastProps.message}
                type={toastProps.type}
                isVisible={toastProps.isVisible}
                onClose={toastProps.onClose}
                action={toastProps.action}
                duration={toastProps.duration}
            />

            {/* ERROR BANNER */}
            {isMapError && viewMode === 'map' && (
                <div className="absolute top-24 left-4 right-4 z-40 animate-in">
                    <div className="bg-brand-red/10 border border-brand-red rounded-xl p-4 shadow-lg flex items-start gap-3">
                        <AlertTriangle className="text-brand-red shrink-0" size={24} />
                        <div>
                            <h4 className="font-bold text-brand-red text-sm">Map Failed to Load</h4>
                            <p className="text-brand-red text-xs mt-1 opacity-80">
                                Google Maps API error. Switching to Simplified Grid Mode. 
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN CONTENT */}
            <div className="flex-1 relative w-full h-full">
                {children}
            </div>
        </div>
    );
};
